import { useParams, Link } from 'react-router-dom';
import { useSharedExtract } from '../hooks/useDeepLink';
import { useDocumentMeta } from '../hooks/useDocumentMeta';

export function SharedExtractPage() {
  const { token } = useParams<{ token: string }>();
  const { data: extract, loading, error } = useSharedExtract(token);

  // Set document meta tags
  useDocumentMeta({
    title: extract ? `"${extract.text.slice(0, 50)}..." | Read l8r` : 'Shared Highlight | Read l8r',
    description: extract ? `${extract.text.slice(0, 150)}... - From ${extract.articleTitle}` : 'A highlight shared via Read l8r',
    ogTitle: extract ? `Highlight from ${extract.articleTitle}` : undefined,
    ogDescription: extract?.text,
    ogType: 'article',
    ogUrl: token ? `${window.location.origin}/e/${token}` : undefined,
    twitterCard: 'summary_large_image'
  });

  if (loading) {
    return (
      <div className="extract-page loading">
        <div className="spinner" />
        <p>Loading...</p>
        <style>{extractStyles}</style>
      </div>
    );
  }

  if (error || !extract) {
    return (
      <div className="extract-page error">
        <div className="error-icon">✂️</div>
        <h1>Extract Not Found</h1>
        <p>{error || 'This shared highlight could not be found.'}</p>
        <Link to="/" className="btn-primary">Go to Read l8r</Link>
        <style>{extractStyles}</style>
      </div>
    );
  }

  return (
    <div className="extract-page">
      {/* Minimal header */}
      <header className="extract-header">
        <Link to="/" className="shared-logo">
          <span>📖</span> Read l8r
        </Link>
      </header>

      {/* Extract card - designed to be visually shareable */}
      <div className="extract-container">
        <div className="extract-card">
          <div className="quote-mark">"</div>
          <blockquote className="extract-text">
            {extract.text}
          </blockquote>
          {extract.note && (
            <div className="extract-note">
              <span className="note-label">Note:</span> {extract.note}
            </div>
          )}
          <footer className="extract-source">
            <span className="from-label">From</span>
            {extract.articleUrl ? (
              <a href={extract.articleUrl} target="_blank" rel="noopener noreferrer">
                {extract.articleTitle}
              </a>
            ) : (
              <span>{extract.articleTitle}</span>
            )}
          </footer>
          {extract.sharedBy && (
            <div className="shared-by-line">
              Highlighted by {extract.sharedBy.name}
            </div>
          )}
        </div>
      </div>

      {/* CTA */}
      <div className="extract-cta">
        <p>Save highlights from any article with Read l8r</p>
        <Link to="/app" className="btn-primary">
          Try it free
        </Link>
      </div>

      <style>{extractStyles}</style>
    </div>
  );
}

const extractStyles = `
  .extract-page {
    min-height: 100vh;
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
    color: #fafaf9;
    display: flex;
    flex-direction: column;
  }

  .extract-page.loading,
  .extract-page.error {
    align-items: center;
    justify-content: center;
    gap: 1rem;
    text-align: center;
    padding: 2rem;
  }

  .error-icon {
    font-size: 4rem;
    opacity: 0.5;
  }

  .spinner {
    width: 40px;
    height: 40px;
    border: 3px solid rgba(249, 115, 22, 0.2);
    border-top-color: #f97316;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .extract-header {
    padding: 1rem 1.5rem;
  }

  .shared-logo {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-weight: 700;
    font-size: 1.25rem;
    color: inherit;
    text-decoration: none;
    opacity: 0.8;
  }

  .extract-container {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
  }

  .extract-card {
    max-width: 600px;
    background: rgba(255, 255, 255, 0.05);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px;
    padding: 3rem;
    position: relative;
  }

  .quote-mark {
    position: absolute;
    top: 1rem;
    left: 1.5rem;
    font-size: 6rem;
    font-family: Georgia, serif;
    color: #f97316;
    opacity: 0.3;
    line-height: 1;
  }

  .extract-text {
    font-family: Georgia, serif;
    font-size: 1.5rem;
    line-height: 1.6;
    margin: 0;
    padding: 0;
    border: none;
    position: relative;
    z-index: 1;
  }

  .extract-note {
    margin-top: 1.5rem;
    padding-top: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.1);
    font-size: 0.9375rem;
    color: rgba(255, 255, 255, 0.7);
    font-style: italic;
  }

  .note-label {
    color: #f97316;
    font-style: normal;
    font-weight: 500;
  }

  .extract-source {
    margin-top: 1.5rem;
    font-size: 0.875rem;
    color: rgba(255, 255, 255, 0.6);
  }

  .from-label {
    color: rgba(255, 255, 255, 0.4);
    margin-right: 0.25rem;
  }

  .extract-source a {
    color: #f97316;
    text-decoration: none;
  }

  .extract-source a:hover {
    text-decoration: underline;
  }

  .shared-by-line {
    margin-top: 1rem;
    font-size: 0.75rem;
    color: rgba(255, 255, 255, 0.4);
    text-transform: uppercase;
    letter-spacing: 0.05em;
  }

  .extract-cta {
    text-align: center;
    padding: 2rem;
    background: rgba(0, 0, 0, 0.2);
  }

  .extract-cta p {
    margin-bottom: 1rem;
    opacity: 0.8;
  }

  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    background: #f97316;
    color: white;
    padding: 0.75rem 1.5rem;
    border-radius: 8px;
    font-weight: 600;
    text-decoration: none;
    border: none;
    cursor: pointer;
    transition: background 0.2s;
  }

  .btn-primary:hover {
    background: #ea580c;
  }

  @media (max-width: 600px) {
    .extract-card {
      padding: 2rem;
    }

    .extract-text {
      font-size: 1.25rem;
    }

    .quote-mark {
      font-size: 4rem;
    }
  }
`;
